import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, Search, Loader2, Copy, Check, AlertTriangle,
  Code, ChevronDown, Zap, CheckCircle, ArrowRight
} from "lucide-react";

const PRIORITY_STYLE = {
  critical: "bg-red-900/30 text-red-400 border-red-800/50",
  high: "bg-orange-900/30 text-orange-400 border-orange-800/50",
  medium: "bg-yellow-900/30 text-yellow-400 border-yellow-800/50",
  low: "bg-[var(--color-surface-alt)] text-[var(--color-text-muted)] border-[var(--color-border)]",
};

export default function FixMySEOPage() {
  const { t } = useLanguage();
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const handleGenerate = async () => {
    if (!url.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/fix-my-seo`, { url: url.trim(), email: email.trim(), language: "en" });
      setReport(r.data);
    } catch {}
    setLoading(false);
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_home || "Home"}</a>
            <a href="/ai-simulator" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_simulator || "Simulator"}</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6" data-testid="fix-hero">
        <div className="max-w-3xl mx-auto text-center">
          <span className="px-3 py-1 bg-[#FFE600]/10 text-[#FFE600] text-sm font-bold mb-6 inline-block">{t.fix_price || "€9 one-time"}</span>
          <h1 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6" data-testid="fix-title">
            {t.fix_title_1 || "FIX MY"} <span className="text-[#a855f7]">{t.fix_title_2 || "SEO"}</span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] mb-10 max-w-xl mx-auto">
            {t.fix_desc || "Get specific, copy-paste SEO fixes. Not vague advice — actual code you can paste into your website right now."}
          </p>

          {!report && (
            <div className="max-w-lg mx-auto space-y-3">
              <input data-testid="fix-url" type="text" value={url} onChange={e => setUrl(e.target.value)}
                placeholder={t.fix_url_ph || "Your website URL"} className="w-full px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]" />
              <input data-testid="fix-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder={t.fix_email_ph || "Your email"} className="w-full px-6 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] text-white font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]" />
              <button data-testid="fix-submit" onClick={handleGenerate} disabled={loading || !url.trim() || !email.trim()}
                className="w-full py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-3">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                {loading ? (t.fix_loading || "Generating fixes...") : (t.fix_btn || "GENERATE MY FIX REPORT")}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      {report && (
        <section className="pb-20 px-6" data-testid="fix-results">
          <div className="max-w-4xl mx-auto">
            {/* Score summary */}
            <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="fix-summary">
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase mb-1">{t.fix_current || "Current Score"}</p>
                <p className="text-5xl font-black font-['JetBrains_Mono'] text-[#a855f7]">{report.overall_score}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-emerald-800/30 p-6 text-center">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase mb-1">{t.fix_after || "After Fixes"}</p>
                <p className="text-5xl font-black font-['JetBrains_Mono'] text-emerald-400">{report.potential_score}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[#FFE600]/30 p-6 text-center">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase mb-1">{t.fix_boost || "Traffic Boost"}</p>
                <p className="text-5xl font-black font-['JetBrains_Mono'] text-[#FFE600]">{report.estimated_traffic_boost}</p>
              </div>
            </div>

            {/* Fixes */}
            <div className="space-y-4" data-testid="fix-list">
              {(report.fixes || []).map((fix, i) => (
                <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] overflow-hidden" data-testid={`fix-item-${i}`}>
                  <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase border ${PRIORITY_STYLE[fix.priority] || PRIORITY_STYLE.medium}`}>
                        {fix.priority}
                      </span>
                      <span className="text-white font-bold font-['Cabinet_Grotesk'] text-sm">{fix.title}</span>
                    </div>
                    <span className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">{fix.category}</span>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Current */}
                    <div>
                      <p className="text-[var(--color-text-muted)] text-[10px] font-['JetBrains_Mono'] uppercase mb-1">{t.fix_problem || "Current (problem)"}</p>
                      <div className="bg-red-950/20 border border-purple-900/30 p-3">
                        <code className="text-red-400 text-xs font-['JetBrains_Mono'] break-all">{fix.current}</code>
                      </div>
                    </div>

                    {/* Fix */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[var(--color-text-muted)] text-[10px] font-['JetBrains_Mono'] uppercase">{t.fix_copy || "Copy-paste fix"}</p>
                        <button onClick={() => handleCopy(fix.fix_code, i)}
                          className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]" data-testid={`copy-fix-${i}`}>
                          {copiedIdx === i ? <><Check className="w-3 h-3 text-emerald-400" />Copied</> : <><Copy className="w-3 h-3" />Copy</>}
                        </button>
                      </div>
                      <div className="bg-emerald-950/20 border border-emerald-900/30 p-3">
                        <code className="text-emerald-400 text-xs font-['JetBrains_Mono'] break-all whitespace-pre-wrap">{fix.fix_code}</code>
                      </div>
                    </div>

                    {/* Impact */}
                    <p className="text-[var(--color-text-muted)] text-xs flex items-start gap-2">
                      <ArrowRight className="w-3 h-3 text-[#FFE600] flex-shrink-0 mt-0.5" />
                      {fix.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-8 bg-[var(--color-surface)] border border-[#FFE600]/30 p-6 text-center">
              <p className="text-[#FFE600] font-bold mb-2">{t.fix_auto || "Want us to fix everything automatically?"}</p>
              <p className="text-[var(--color-text-muted)] text-sm mb-4">{t.fix_auto_desc || "SEO Autopilot applies all fixes to your WordPress/Shopify site with one click."}</p>
              <a href="/products/seo-autopilot" className="inline-flex items-center gap-2 px-8 py-3 bg-[#FFE600] text-black font-bold uppercase hover:bg-yellow-300">
                SEO Autopilot — €79/mo <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
